import { useState, useEffect, useCallback } from "react";

const INITIAL_LOAD_THRESHOLD = 12;
const DEFAULT_PAGE_SIZE = 12;

interface UseLazyLoadingOptions {
  totalItems: number;
  pageSize?: number;
  initialPageSize?: number;
  // Should load more items
  shouldLoad?: boolean;
}

interface UseLazyLoadingReturn {
  visibleCount: number;
  displayCount: number;
  hasMore: boolean;
  loadMore: () => void;
  resetLoading: () => void;
  loadingIndicator: {
    show: boolean;
    current: number;
    total: number;
  };
}

export const useLazyLoading = ({
  totalItems,
  pageSize = DEFAULT_PAGE_SIZE,
  initialPageSize,
  shouldLoad,
}: UseLazyLoadingOptions): UseLazyLoadingReturn => {
  const baseInitialSize = initialPageSize || pageSize;

  // Simple initial size calculation - always stick to base initial size
  const calculateInitialSize = useCallback(() => {
    // Always return the base initial size (12)
    // Lazy loading will handle loading more as needed
    return baseInitialSize;
  }, [baseInitialSize]);

  const initialSize = calculateInitialSize();
  const [visibleCount, setVisibleCount] = useState(initialSize);

  // Reset loading when totalItems changes
  useEffect(() => {
    const newInitialSize = calculateInitialSize();
    setVisibleCount(newInitialSize);
  }, [totalItems, calculateInitialSize]);

  // Simple loading logic
  useEffect(() => {
    if (shouldLoad) {
      setVisibleCount((prev) => {
        const hasMore = prev < totalItems;
        if (hasMore && prev >= INITIAL_LOAD_THRESHOLD) {
          return Math.min(prev + pageSize, totalItems);
        }
        return prev;
      });
    }
  }, [shouldLoad, totalItems, pageSize]);

  // Load more items
  const loadMore = useCallback(() => {
    if (visibleCount < totalItems) {
      setVisibleCount((prev) => Math.min(prev + pageSize, totalItems));
    }
  }, [visibleCount, totalItems, pageSize]);

  // Reset to initial state
  const resetLoading = useCallback(() => {
    setVisibleCount(initialSize);
  }, [initialSize]);

  const displayCount = Math.min(visibleCount, totalItems);
  const hasMore = visibleCount < totalItems;

  return {
    visibleCount,
    displayCount,
    hasMore,
    loadMore,
    resetLoading,
    loadingIndicator: {
      show: hasMore,
      current: visibleCount,
      total: totalItems,
    },
  };
};

export default useLazyLoading;
